import { FC, useState } from 'react';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Category } from '@/__generated_graphql__/graphql';
import { CategoriesFilterMobile } from '@/components/CategoriesFilterMobile/CategoriesFilterMobile';
import { DrawerModal } from '@/components/DrawerModal/DrawerModal';
import { DEFAULT_VALUES } from '@/components/Filters/Filters.constants';
import { useCategoryModal } from '@/hooks/useCategoryModal';

import { getCategoriesToSubmit } from './CategoryModalMobile.helpers';
import * as S from './CategoryModalMobile.styles';

type Props = {
  externalMethods?: UseFormReturn<Pick<typeof DEFAULT_VALUES, 'categories'>>;
  onClose: () => void;
  value?: Category[];
  onChange: (categories: Category[]) => void;
  isOpen?: boolean;
  onConfirmCallback?: (categories: Category[]) => void;
};

export const CategoryModalMobile: FC<Props> = ({
  externalMethods,
  onClose,
  value = [],
  onChange,
  isOpen = true,
  onConfirmCallback
}) => {
  const [activeCategory, setActiveCategory] = useState<Category | null>(null);
  const { t } = useTranslation();

  const handleCloseCategory = () => setActiveCategory(null);

  const { methods, handleClose, handleConfirm, id } = useCategoryModal({
    externalMethods,
    value,
    onChange,
    onClose,
    onCloseCategory: handleCloseCategory
  });

  const onSubmit = (formData: { categories: Category[] }) => {
    const categoriesToSubmit = getCategoriesToSubmit(
      activeCategory,
      formData.categories
    );

    handleConfirm({ categories: categoriesToSubmit });
    onConfirmCallback?.(categoriesToSubmit);
    setActiveCategory(null);
  };

  const title = activeCategory
    ? activeCategory.name
    : t('CategoryModal.mobile_title');

  return (
    <DrawerModal
      onBack={activeCategory ? handleCloseCategory : undefined}
      title={title}
      isOpen={isOpen}
      onClose={handleClose}
      submitLabel={t('CategoryModal.confirm_button')}
      formId={id}
    >
      <S.ContentWrapper>
        <FormProvider {...methods}>
          <form id={id} onSubmit={methods.handleSubmit(onSubmit)}>
            <CategoriesFilterMobile
              activeCategory={activeCategory}
              setActiveCategory={setActiveCategory}
            />
          </form>
        </FormProvider>
      </S.ContentWrapper>
    </DrawerModal>
  );
};
